/**
 * GraphQL query for a dynamic CMS page, fetched by `slug` from the `article` collection type in the
 * CMS (`cms.service.app/src/api/article`). Unlike the landing single type, articles are a localized
 * collection, so the page is resolved with a `slug` filter and the first match is used.
 *
 * Articles are shared across tenants (the same slug exists under many), so the query is filtered by
 * both `slug` and `tenant` — the Tyme Group tenant comes from `environment.tenant` (`.env`
 * `CMS_TENANT`). The first match is used.
 *
 * The `sections` dynamic zone reuses the same Strapi section components as the landing page; only the
 * subset the frontend renders is selected here (faq-block, widget-list-block, success-stories,
 * static-block, slider-block, cards-block, tabs-block, how-it-works-block). Their field selections
 * mirror {@link TYME_GROUP_LANDING_QUERY} — any unmapped section `__typename` still renders via the
 * dispatcher's unknown-section fallback. Only components the `article` dynamic zone actually lists
 * may be selected here (see `cms.service.app/src/api/article/content-types/article/schema.json`) —
 * an inline fragment on a component outside that union fails the whole query.
 */
export const TYME_GROUP_PAGE_QUERY: string = `
fragment MediaFields on UploadFile {
  url
  alternativeText
  width
  height
  name
  mime
  # Strapi's generated derivatives (thumbnail/small/medium/large) as a JSON scalar. The cms-image
  # component turns them into a srcset; without them the browser can only fetch the full-size
  # original (three 2000x2000 avatars rendered at 104px were 15 MB of a 16 MB page).
  formats
}

fragment ButtonFields on ComponentButtonButton {
  id
  style
  label
  link
  isExternalLink
  queryParams
  target
  buttonAlignment
  customClass
  buttonIcon {
    ...MediaFields
  }
}

fragment AccordionItemFields on ComponentSharedAccordionItem {
  id
  itemLabel
  itemContent
  itemLabelImage {
    ...MediaFields
  }
}

fragment WidgetListItemFields on ComponentSharedWidgetListItem {
  id
  itemLabel
  itemListMarker {
    ...MediaFields
  }
}

query TymeGroupPage($slug: String!, $tenant: String!, $status: PublicationStatus, $locale: I18NLocaleCode) {
  articles(
    filters: { slug: { eq: $slug }, tenant: { eq: $tenant } }
    status: $status
    locale: $locale
    pagination: { limit: 1 }
  ) {
    documentId
    slug
    locale
    sections {
      __typename
      ... on ComponentSectionFaqBlock {
        id
        content
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        styles
        blockClassName
        accordionItems {
          ...AccordionItemFields
        }
      }
      ... on ComponentSectionWidgetListBlock {
        id
        imageBlockTitle
        titleContent
        content
        blockId
        blockClassName
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        styles
        widgetListBlockContentPosition
        imageBlockWidthPct
        imageMaxWidthPx
        imageAlignment
        contentBlockWidthPct
        contentBlockMaxWidthPx
        contentAlignment
        image {
          ...MediaFields
        }
        backgroundImage {
          ...MediaFields
        }
        buttonLink {
          ...ButtonFields
        }
        items {
          ...WidgetListItemFields
        }
        search {
          id
          content
          enabledForExpert
          enabledForSeeker
          enabledForGuest
          blockId
          styles
          blockClassName
          mode
        }
      }
      ... on ComponentSectionSuccessStories {
        id
        content
        blockId
        styles
        blockClassName
        storiesList {
          id
          fullName
          shortInfo
          story
          avatarImg {
            ...MediaFields
          }
        }
      }
      ... on ComponentSectionStaticBlock {
        id
        content
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        styles
        blockClassName
        staticBlockContentBlockWidthPct
        staticBlockContentBlockMaxWidthPx
        staticBlockContentAlignment
        signUpButton {
          id
          style
          label
          buttonAlignment
        }
        buttonLink {
          ...ButtonFields
        }
        items {
          ...WidgetListItemFields
        }
        staticBlockBackgroundImage {
          ...MediaFields
        }
      }
      ... on ComponentSectionSliderBlock {
        id
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        styles
        blockClassName
        titleContent
        sliderMode
        sliderItems {
          id
          sliderItemContent
          sliderTabName
          sliderImage {
            ...MediaFields
          }
        }
      }
      ... on ComponentSectionCardsBlock {
        id
        titleContent
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        styles
        blockClassName
        blockId
        position
        backgroundImageDesktop {
          ...MediaFields
        }
        backgroundImageMobile {
          ...MediaFields
        }
        buttonsList {
          ...ButtonFields
        }
        cardItems {
          id
          tagLabel
          content
          image {
            ...MediaFields
          }
          buttonLink {
            ...ButtonFields
          }
        }
      }
      ... on ComponentSectionTabsBlock {
        id
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        styles
        blockClassName
        content
        tabsPosition
        tabsItems {
          ...AccordionItemFields
        }
      }
      ... on ComponentSectionHowItWorksBlock {
        id
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        styles
        blockClassName
        titleContent
        footerContent
        items {
          id
          content
          image {
            ...MediaFields
          }
        }
        howItWorksBackgroundImage {
          ...MediaFields
        }
      }
      ... on Error {
        code
        message
      }
    }
    seo {
      id
      metaTitle
      metaDescription
      keywords
      metaRobots
      metaViewport
      canonicalURL
      structuredData
      metaImage {
        ...MediaFields
      }
      metaSocial {
        id
        socialNetwork
        title
        description
        image {
          ...MediaFields
        }
      }
    }
  }
}
`;
